import { defineStore } from 'pinia';
import { useToast } from 'vue-toastification';

import { pinia } from '@/main';
import main from '@/main';

const toast = useToast();

interface SocketMessage {
  type: string;
  data?: any;
  serverId?: string;
  timestamp?: number;
}

interface SocketStore {
  // 连接状态
  isConnected: boolean;
  // 原始消息内容
  message: string;
  // 解析后的消息
  parsedMessage: SocketMessage | null;
  // 重新连接错误
  reconnectError: boolean;
  // 心跳消息发送时间
  heartBeatInterval: number;
  // 心跳定时器
  heartBeatTimer: number;
  // 服务器状态数据缓存
  serverStatuses: Record<string, any>;
  // 服务器指标数据缓存
  serverMetrics: Record<string, any>;
  // 插件列表缓存
  pluginLists: Record<string, any[]>;
  // 玩家事件缓存
  playerEvents: Record<string, any[]>;
  // 日志数据缓存
  logData: Record<string, any[]>;
  // 命令结果缓存
  commandResults: Record<string, any>;
  // 错误报告缓存
  errorReports: Record<string, any[]>;
}

/**
 * Normalize error report fields from Java WebSocket camelCase to snake_case
 * matching the D1 database column names used by the frontend.
 */
function normalizeErrorReport(report: any): any {
  if (!report || typeof report !== 'object') return report;
  return {
    fingerprint: report.fingerprint,
    error_code: report.errorCode ?? report.error_code,
    message: report.message,
    stack_trace: report.stackTrace ?? report.stack_trace,
    module_name: report.moduleName ?? report.module_name,
    class_name: report.className ?? report.class_name,
    method_name: report.methodName ?? report.method_name,
    trigger_type: report.triggerType ?? report.trigger_type,
    trigger_detail: report.triggerDetail ?? report.trigger_detail,
    player_name: report.playerName ?? report.player_name,
    player_uuid: report.playerUuid ?? report.player_uuid,
    server_tps: report.serverTPS ?? report.server_tps,
    server_memory_pct: report.serverMemoryPct ?? report.server_memory_pct,
    player_count: report.playerCount ?? report.player_count,
    occurrence_count: report.occurrenceCount ?? report.occurrence_count ?? 1,
    first_seen_at: report.timestamp ?? report.first_seen_at,
    last_seen_at: report.timestamp ?? report.last_seen_at,
    resolved: report.resolved ?? 0,
  };
}

export const useSocketStore = defineStore({
  id: 'socket',
  state: (): SocketStore => ({
    // 连接状态
    isConnected: false,
    // 原始消息内容
    message: '',
    // 解析后的消息
    parsedMessage: null,
    // 重新连接错误
    reconnectError: false,
    // 心跳消息发送时间
    heartBeatInterval: 30000, // 30秒心跳
    // 心跳定时器
    heartBeatTimer: 0,
    // 数据缓存
    serverStatuses: {},
    serverMetrics: {},
    pluginLists: {},
    playerEvents: {},
    logData: {},
    commandResults: {},
    errorReports: {},
  }),
  getters: {
    getServerStatus: (state) => (serverId: string) => state.serverStatuses[serverId],
    getServerMetrics: (state) => (serverId: string) => state.serverMetrics[serverId],
    getPluginList: (state) => (serverId: string) => state.pluginLists[serverId] || [],
    getPlayerEvents: (state) => (serverId: string) => state.playerEvents[serverId] || [],
    getLogData: (state) => (serverId: string) => state.logData[serverId] || [],
    getCommandResult: (state) => (commandId: string) => state.commandResults[commandId],
    getErrorReports: (state) => (serverId: string) => state.errorReports[serverId] || [],
  },
  actions: {
    // 连接打开
    SOCKET_ONOPEN(event: Event) {
      main.config.globalProperties.$socket = event.currentTarget;
      this.isConnected = true;
      console.log('WebSocket connected at', new Date().toLocaleString());

      // 连接成功时启动定时发送心跳消息，避免被服务器断开连接
      this.heartBeatTimer = window.setInterval(() => {
        if (this.isConnected && main.config.globalProperties.$socket) {
          const heartbeat = JSON.stringify({ type: 'ping' });
          main.config.globalProperties.$socket.send(heartbeat);
        }
      }, this.heartBeatInterval);
    },

    // 连接关闭
    SOCKET_ONCLOSE(event: Event) {
      this.isConnected = false;
      // 连接关闭时停掉心跳消息
      window.clearInterval(this.heartBeatTimer);
      this.heartBeatTimer = 0;
      console.log('WebSocket connection closed at', new Date().toLocaleString());
      console.log(event);
    },

    // 发生错误
    SOCKET_ONERROR(event: Event) {
      toast.error('连接服务器失败，请检查网络连接或联系管理员');
      console.error('WebSocket error:', event);
    },

    // 收到服务端发送的消息
    // vue-native-websocket-vue3 with format:"json" already parses the JSON,
    // so `message` is a parsed object, not a raw string.
    SOCKET_ONMESSAGE(message: SocketMessage | string) {
      if (typeof message === 'string') {
        // Fallback: if the library passes a raw string, parse it
        this.message = message;
        try {
          const parsed: SocketMessage = JSON.parse(message);
          this.parsedMessage = parsed;
          this.handleParsedMessage(parsed);
        } catch (error) {
          console.warn('Failed to parse WebSocket message:', message);
        }
      } else {
        // Normal path: message is already a parsed object
        this.message = message.type || '';
        this.parsedMessage = message;
        this.handleParsedMessage(message);
      }
    },

    // 处理解析后的消息
    handleParsedMessage(message: SocketMessage) {
      const { type, data, serverId } = message;

      switch (type) {
        case 'pong':
          // 心跳响应，无需处理
          break;

        case 'server_status':
          if (serverId) {
            if (data) {
              // 服务器在线，存储状态数据（保持与HTTP格式一致：{status: ..., serverId, timestamp}）
              this.serverStatuses[serverId] = {
                serverId,
                status: data,
                timestamp: Date.now(),
              };
            } else {
              // 服务器离线，存储离线状态
              this.serverStatuses[serverId] = {
                serverId: serverId,
                status: null,
                timestamp: Date.now(),
                offline: true
              };
            }
          }
          break;

        case 'plugin_list':
          if (serverId) {
            if (data && data.plugins) {
              this.pluginLists[serverId] = data.plugins;
            } else {
              // data 为 null 或没有 plugins 时，表示服务器离线，清空插件列表
              this.pluginLists[serverId] = [];
            }
          }
          break;

        case 'player_event':
          if (serverId && data) {
            if (!this.playerEvents[serverId]) {
              this.playerEvents[serverId] = [];
            }
            this.playerEvents[serverId].unshift(data);
            // 保持最新的100个事件
            if (this.playerEvents[serverId].length > 100) {
              this.playerEvents[serverId] = this.playerEvents[serverId].slice(0, 100);
            }
          }
          break;

        case 'metrics_data':
          if (serverId) {
            if (data) {
              // 展平 metrics 数据到顶层（data IS the metrics object, no .metrics wrapper）
              this.serverMetrics[serverId] = {
                serverId: serverId,
                timestamp: Date.now(),
                // 展平性能指标数据
                memoryUsage: data.serverPerformance?.memoryUsage || 0,
                averageTPS: data.serverPerformance?.averageTPS || 0,
                diskUsage: data.serverPerformance?.diskUsage || 0,
                // 玩家活动数据
                currentOnline: data.playerActivity?.currentOnline || 0,
                maxPlayers: data.playerActivity?.maxPlayers || 0,
                // 插件使用数据
                loadedWorlds: data.pluginUsage?.loadedWorlds || 0,
                enabledPlugins: data.pluginUsage?.enabledPlugins || 0,
                // 保留原始数据结构以防其他地方需要
                rawData: data
              };
            } else {
              // data 为 null 时，表示服务器离线
              this.serverMetrics[serverId] = {
                serverId: serverId,
                timestamp: Date.now(),
                offline: true,
                memoryUsage: 0,
                averageTPS: 0,
                diskUsage: 0,
                currentOnline: 0,
                maxPlayers: 0,
                loadedWorlds: 0,
                enabledPlugins: 0
              };
            }
          }
          break;

        case 'log_stream':
          if (serverId && data) {
            if (!this.logData[serverId]) {
              this.logData[serverId] = [];
            }
            this.logData[serverId].unshift(data);
            // 保持最新的1000条日志
            if (this.logData[serverId].length > 1000) {
              this.logData[serverId] = this.logData[serverId].slice(0, 1000);
            }
          }
          break;

        case 'log_batch':
          if (serverId && data && Array.isArray(data)) {
            if (!this.logData[serverId]) {
              this.logData[serverId] = [];
            }
            for (const logEntry of data) {
              if (logEntry && typeof logEntry === 'object') {
                this.logData[serverId].unshift(logEntry);
              }
            }
            // 保持最新的1000条日志
            if (this.logData[serverId].length > 1000) {
              this.logData[serverId] = this.logData[serverId].slice(0, 1000);
            }
          }
          break;

        case 'log_stream_response':
          // Control responses from log stream (start/stop/status) — not actual log entries
          break;

        case 'command_result':
          if (data && data.commandId) {
            this.commandResults[data.commandId] = data;
          }
          break;

        case 'error_report':
          if (serverId && data) {
            if (!this.errorReports[serverId]) {
              this.errorReports[serverId] = [];
            }
            // data may be a single report or an array
            const reports = Array.isArray(data) ? data : [data];
            for (const report of reports) {
              // Normalize camelCase (from Java WS) to snake_case (matching D1 columns)
              this.errorReports[serverId].unshift(normalizeErrorReport(report));
            }
            // 保持最新的50条错误报告
            if (this.errorReports[serverId].length > 50) {
              this.errorReports[serverId] = this.errorReports[serverId].slice(0, 50);
            }
          }
          break;

        case 'file_operation_result':
          // 文件操作结果可以通过事件系统通知相关组件
          console.log('File operation result:', data);
          break;

        case 'backup_progress':
          // 备份进度更新
          console.log('Backup progress:', data);
          break;

        default:
          console.log('Unknown WebSocket message type:', type, data);
      }
    },

    // 自动重连
    SOCKET_RECONNECT(count: number) {
      console.info('WebSocket attempting to reconnect, attempt:', count);
    },

    // 重连错误
    SOCKET_RECONNECT_ERROR() {
      this.reconnectError = true;
      toast.error('WebSocket重连失败，请刷新页面重试');
    },

    // 清除服务器相关数据
    clearServerData(serverId: string) {
      delete this.serverStatuses[serverId];
      delete this.serverMetrics[serverId];
      delete this.pluginLists[serverId];
      delete this.playerEvents[serverId];
      delete this.logData[serverId];
      delete this.errorReports[serverId];
    },

    // 发送消息到WebSocket
    sendMessage(message: any) {
      if (this.isConnected && main.config.globalProperties.$socket) {
        const messageStr = typeof message === 'string' ? message : JSON.stringify(message);
        main.config.globalProperties.$socket.send(messageStr);
        return true;
      }
      return false;
    },
  },
});

// Need to be used outside the setup
export function useSocketStoreWithOut() {
  return useSocketStore(pinia);
}
